import { useEffect, useState, useRef, useCallback } from "react";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";

/**
 * Comprehensive table state that can be preserved across navigation
 */
export type PreservedTableState = {
  pageIndex: number;
  pageSize: number;
  search: string;
  searchInput: string;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  filterValue: any[];
  tab?: number; // existing numeric tab
  tabString?: string; // ✅ NEW string-based tab
  version?: string;
};

export type UsePreservedTableStateOptions = {
  key: string;

  /** Numeric tab support (1,2,3...) */
  tabIndex?: number;

  /** ✅ String tab support ("log","draft","enquiry_log") */
  tabString?: string;

  defaults?: Partial<PreservedTableState>;
  clearOnMount?: boolean;
  version?: string;
  onStateRestored?: (state: PreservedTableState) => void;
  onStateSaved?: (state: PreservedTableState) => void;
};

const DEFAULT_VERSION = "1.0.0";

export default function usePreservedTableState(
  options: UsePreservedTableStateOptions
) {
  const {
    key,
    tabIndex,
    tabString,
    defaults = {},
    clearOnMount = false,
    version = DEFAULT_VERSION,
    onStateRestored,
    onStateSaved,
  } = options;

  /**
   * 🔑 Build stable storage key
   * Priority: tabString → tabIndex → default
   */
  const resolvedTabKey =
    tabString !== undefined && tabString !== null
      ? `tab-${tabString}`
      : tabIndex !== undefined
      ? `tab-${tabIndex}`
      : undefined; // no default suffix

  const storageKey = resolvedTabKey
    ? `table-state-${key}-${resolvedTabKey}`
    : `table-state-${key}`;

  const isFirstRender = useRef(true);
  const hasRestoredState = useRef(false);
  const isRestoringState = useRef(true);

  /**
   * Validate & migrate stored state
   */
  const validateAndMigrateState = useCallback(
    (state: any): PreservedTableState | null => {
      if (!state || typeof state !== "object") return null;

      if (state.version && state.version !== version) {
        console.warn(`State version mismatch for ${key}. Resetting state.`);
        return null;
      }

      return {
        pageIndex:
          typeof state.pageIndex === "number" && state.pageIndex >= 0
            ? state.pageIndex
            : defaults.pageIndex ?? 0,
        pageSize:
          typeof state.pageSize === "number" && state.pageSize > 0
            ? state.pageSize
            : defaults.pageSize ?? 10,
        search: typeof state.search === "string" ? state.search : "",
        searchInput:
          typeof state.searchInput === "string" ? state.searchInput : "",
        sorting: Array.isArray(state.sorting) ? state.sorting : [],
        columnFilters: Array.isArray(state.columnFilters)
          ? state.columnFilters
          : [],
        filterValue: Array.isArray(state.filterValue) ? state.filterValue : [],
        tab: tabIndex ?? state.tab,
        tabString: tabString ?? state.tabString,
        version,
      };
    },
    [defaults, key, tabIndex, tabString, version]
  );

  /**
   * Default state
   */
  const getDefaultState = useCallback(
    (): PreservedTableState => ({
      pageIndex: defaults.pageIndex ?? 0,
      pageSize: defaults.pageSize ?? 10,
      search: defaults.search ?? "",
      searchInput: defaults.searchInput ?? "",
      sorting: defaults.sorting ?? [],
      columnFilters: defaults.columnFilters ?? [],
      filterValue: defaults.filterValue ?? [],
      tab: tabIndex ?? defaults.tab,
      tabString: tabString ?? defaults.tabString,
      version,
    }),
    [defaults, tabIndex, tabString, version]
  );

  /**
   * Load initial state
   */
  const loadInitialState = useCallback((): PreservedTableState => {
    if (clearOnMount) {
      sessionStorage.removeItem(storageKey);
      return getDefaultState();
    }

    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const validated = validateAndMigrateState(parsed);
        if (validated) {
          hasRestoredState.current = true;
          onStateRestored?.(validated);
          return validated;
        }
      }
    } catch {
      sessionStorage.removeItem(storageKey);
    }

    return getDefaultState();
  }, [
    clearOnMount,
    getDefaultState,
    onStateRestored,
    storageKey,
    validateAndMigrateState,
  ]);

  const [state, setState] = useState<PreservedTableState>(loadInitialState);

  /**
   * Mark restore complete
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      isRestoringState.current = false;
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Save state
   */
  const saveState = useCallback(
    (nextState: PreservedTableState) => {
      try {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({ ...nextState, version })
        );
        onStateSaved?.(nextState);
      } catch {
        console.warn(`Failed to save state: ${storageKey}`);
      }
    },
    [onStateSaved, storageKey, version]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    saveState(state);
  }, [state, saveState]);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    return () => saveState(stateRef.current);
  }, [saveState]);

  /* ---------------- setters ---------------- */

  const setPageIndex = (v: number | ((p: number) => number)) =>
    setState((s) => ({
      ...s,
      pageIndex: typeof v === "function" ? v(s.pageIndex) : v,
    }));

  const setPageSize = (v: number | ((p: number) => number)) =>
    setState((s) => ({
      ...s,
      pageSize: typeof v === "function" ? v(s.pageSize) : v,
      pageIndex: 0,
    }));

  const setSearch = (v: string | ((p: string) => string)) =>
    setState((s) => {
      const nextValue = typeof v === "function" ? v(s.search) : v;
      const isChanged = nextValue !== s.search;
      return {
        ...s,
        search: nextValue,
        pageIndex: isChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });

  const setSearchInput = (v: string | ((p: string) => string)) =>
    setState((s) => ({
      ...s,
      searchInput: typeof v === "function" ? v(s.searchInput) : v,
    }));

  const setSorting = (v: SortingState | ((p: SortingState) => SortingState)) =>
    setState((s) => {
      const nextValue = typeof v === "function" ? v(s.sorting) : v;
      const isChanged = JSON.stringify(nextValue) !== JSON.stringify(s.sorting);
      return {
        ...s,
        sorting: nextValue,
        pageIndex: isChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });

  const setColumnFilters = (
    v: ColumnFiltersState | ((p: ColumnFiltersState) => ColumnFiltersState)
  ) =>
    setState((s) => {
      const nextValue = typeof v === "function" ? v(s.columnFilters) : v;
      const isChanged =
        JSON.stringify(nextValue) !== JSON.stringify(s.columnFilters);
      return {
        ...s,
        columnFilters: nextValue,
        pageIndex: isChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });

  const setFilterValue = (v: any[] | ((p: any[]) => any[])) =>
    setState((s) => {
      const nextValue = typeof v === "function" ? v(s.filterValue) : v;
      const isChanged =
        JSON.stringify(nextValue) !== JSON.stringify(s.filterValue);
      return {
        ...s,
        filterValue: nextValue,
        pageIndex: isChanged && !isRestoringState.current ? 0 : s.pageIndex,
      };
    });

  const setTab = (v: number) =>
    setState((s) => ({
      ...s,
      tab: v,
    }));

  const setTabString = (v: string) =>
    setState((s) => ({
      ...s,
      tabString: v,
    }));

  /* ---------------- utilities ---------------- */

  const resetState = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setState(getDefaultState());
  }, [getDefaultState, storageKey]);

  const clearState = useCallback(() => {
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  return {
    ...state,

    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    setTab,
    setTabString,

    resetState,
    clearState,

    hasRestoredState: hasRestoredState.current,
    storageKey,
  };
}
